import { useState } from "react"
import { useNavigate } from "react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import ModuleLayout from "@/components/admin/ModuleLayout"
import { RowActionsMenu } from "@/components/admin/RowActionsMenu"
import { TempPasswordReveal } from "@/components/admin/TempPasswordReveal"
import Badge from "@/components/ui/badge/Badge"
import DataTable, { type DataTableColumn } from "@/components/ui/table/DataTable"
import { useServerTable } from "@/hooks/useServerTable"
import { staffUsersAdmin, type StaffUser, type StaffUserRoleAssignment } from "@/lib/api/staffUsersClient"
import { staffRolesAdmin } from "@/lib/api/staffRolesClient"
import { getCurrentUser } from "@/lib/auth"
import { toastError } from "@/lib/toast"
import { applyOptimistic, rollbackOptimistic } from "@/lib/optimisticMutation"
import { showUndoToast } from "@/lib/undoToast"
import { LockIcon, CheckCircleIcon, CloseIcon } from "@/icons"
import { STAFF_PROFILE_LABELS, STAFF_PROFILES, type StaffProfile } from "@alascorporation/access-control"

// El <select> de rol combina los 3 perfiles predefinidos (legacy, envían
// `profile`) con los roles personalizados creados en Sistema → Roles y
// permisos (envían `roleId`) — un solo valor de string codifica cuál es cuál
// para poder usarlo directo como `value`/`onChange` de un <select>.
type RoleSelection = `profile:${StaffProfile}` | `role:${string}`

function roleSelectionToAssignment(selection: RoleSelection): StaffUserRoleAssignment {
  const [kind, value] = selection.split(":") as ["profile" | "role", string]
  return kind === "profile" ? { profile: value as StaffProfile } : { roleId: value }
}

function currentRoleSelection(user: StaffUser): RoleSelection {
  return user.roleId ? `role:${user.roleId}` : `profile:${user.role}`
}

interface StaffUsersPayload {
  users: StaffUser[]
  total: number
}

export default function StaffUsersPage() {
  const currentUser = getCurrentUser()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [search, setSearch] = useState("")
  const [activeFilter, setActiveFilter] = useState<"" | "active" | "inactive">("")
  const [reveal, setReveal] = useState<{ label: string; value: string } | null>(null)

  // Roles personalizados disponibles para asignar, además de los 3 perfiles
  // predefinidos de siempre — ver design.md, "catálogo en código, asignación en BD".
  const { data: customRoles = [] } = useQuery({
    queryKey: ["staff-roles"],
    queryFn: () => staffRolesAdmin.list(),
    select: (roles) => roles.filter((r) => !r.isBuiltIn),
  })

  const {
    rows: users,
    total,
    payload,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    error: loadError,
    reload,
    queryKey,
  } = useServerTable<StaffUser>({
    endpoint: "/admin/staff-users",
    defaultPageSize: 20,
    defaultSort: { column: "createdAt", dir: "desc" },
    // El filtro de estado viaja al servidor: así el total y la paginación son reales con cualquier cantidad de filas.
    params: { search, isActive: activeFilter === "active" ? "true" : activeFilter === "inactive" ? "false" : undefined },
    extractRows: (data) => data.users ?? [],
    extractTotal: (data) => data.total ?? 0,
  })
  // Totales globales de cuentas (no dependen de la página ni del filtro).
  const summary = (payload as { summary?: { active: number; inactive: number } } | undefined)?.summary

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; body: Partial<StaffUserRoleAssignment> & { isActive?: boolean } }) =>
      staffUsersAdmin.update(vars.id, vars.body),
  })

  // Cambiar rol o activar/desactivar aplica de inmediato a la caché de la
  // página actual y ofrece deshacer — ver design.md, receta "CRUD de lista".
  const handleUpdate = (
    user: StaffUser,
    body: Partial<StaffUserRoleAssignment> & { isActive?: boolean },
    undoMessage: string,
  ) => {
    void (async () => {
      const previous = await applyOptimistic<StaffUsersPayload>(qc, queryKey, (prev) =>
        prev ? { ...prev, users: prev.users.map((u) => (u.id === user.id ? { ...u, ...body } : u)) } : prev,
      )
      showUndoToast({
        message: undoMessage,
        onUndo: () => rollbackOptimistic(qc, queryKey, previous),
        onCommit: async () => {
          try {
            await updateMutation.mutateAsync({ id: user.id, body })
            reload()
          } catch {
            rollbackOptimistic(qc, queryKey, previous)
            toastError("No se pudo guardar el cambio.")
          }
        },
      })
    })()
  }

  // Pesimista a propósito, no pendiente: misma razón que createMutation —
  // produce una contraseña temporal generada por el servidor.
  const resetPasswordMutation = useMutation({
    mutationFn: (id: string) => staffUsersAdmin.resetPassword(id),
    onSuccess: (result, id) => {
      const user = users.find((u) => u.id === id)
      setReveal({ label: `Nueva contraseña temporal para ${user?.name ?? "la cuenta"}`, value: result.tempPassword })
    },
    onError: (err) => toastError(err),
  })

  const columns: DataTableColumn<StaffUser>[] = [
    {
      key: "name",
      label: "Nombre",
      sortable: true,
      render: (user) => <span className="font-medium text-gray-800 dark:text-white/90">{user.name}</span>,
    },
    { key: "email", label: "Correo", sortable: true, render: (user) => <span className="text-gray-500">{user.email}</span> },
    {
      key: "role",
      label: "Rol",
      sortable: true,
      render: (user) => (
        <select
          aria-label={`Rol de ${user.name}`}
          className="min-h-11 rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900"
          value={currentRoleSelection(user)}
          onChange={(e) => {
            const selection = e.target.value as RoleSelection
            const label = selection.startsWith("role:")
              ? (customRoles.find((r) => r.id === selection.slice("role:".length))?.name ?? "un rol personalizado")
              : STAFF_PROFILE_LABELS[selection.slice("profile:".length) as StaffProfile]
            handleUpdate(user, roleSelectionToAssignment(selection), `Rol de ${user.name} cambiado a ${label}`)
          }}
        >
          <optgroup label="Perfiles predefinidos">
            {STAFF_PROFILES.map((profile) => (
              <option key={profile} value={`profile:${profile}`}>
                {STAFF_PROFILE_LABELS[profile]}
              </option>
            ))}
          </optgroup>
          {customRoles.length > 0 ? (
            <optgroup label="Roles personalizados">
              {customRoles.map((role) => (
                <option key={role.id} value={`role:${role.id}`}>
                  {role.name}
                </option>
              ))}
            </optgroup>
          ) : null}
        </select>
      ),
    },
    {
      key: "isActive",
      label: "Estado",
      render: (user) => (
        <>
          <Badge color={user.isActive ? "success" : "light"} size="sm">
            {user.isActive ? "Activo" : "Inactivo"}
          </Badge>
          {user.mustChangePassword ? (
            <span className="ml-1.5 text-xs text-warning-700 dark:text-warning-400">Debe cambiar su contraseña</span>
          ) : null}
        </>
      ),
    },
    {
      key: "actions",
      label: "",
      headerAriaLabel: "Acciones",
      align: "right",
      render: (user) => {
        const isSelf = user.id === currentUser?.id
        const isResetting = resetPasswordMutation.isPending && resetPasswordMutation.variables === user.id
        return (
          <div className="flex justify-end">
            <RowActionsMenu
              actions={[
                {
                  label: isResetting ? "Generando..." : "Restablecer contraseña",
                  icon: <LockIcon />,
                  onClick: () => resetPasswordMutation.mutate(user.id),
                  disabled: isResetting,
                  requires: "staff:manage-accounts:edit",
                },
                {
                  label: user.isActive ? "Desactivar" : "Activar",
                  icon: user.isActive ? <CloseIcon /> : <CheckCircleIcon />,
                  onClick: () =>
                    handleUpdate(
                      user,
                      { isActive: !user.isActive },
                      `${user.name} ${user.isActive ? "desactivado" : "activado"}`,
                    ),
                  variant: user.isActive ? "danger" : "default",
                  disabled: isSelf && user.isActive,
                  requires: "staff:manage-accounts:edit",
                },
              ]}
            />
          </div>
        )
      },
    },
  ]

  const inactiveCount = summary?.inactive ?? 0

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Sistema", "Usuarios"]}
      title="Usuarios"
      subtitle="Cuentas de administradores, docentes y gestores CCCD"
      tabs={[{ label: "Usuarios", to: "/settings/users", active: true }]}
      metrics={[
        { label: "Cuentas de staff", value: summary ? String(summary.active + summary.inactive) : "—" },
        { label: "Activas", value: summary ? String(summary.active) : "—", tone: "good" },
        {
          label: "Inactivas",
          value: summary ? String(inactiveCount) : "—",
          tone: inactiveCount > 0 ? "warning" : "neutral",
          hint: "No pueden iniciar sesión",
        },
      ]}
      toolbar={{
        search: {
          value: search,
          onChange: setSearch,
          placeholder: "Buscar por nombre o correo...",
        },
        filters: [
          {
            type: "select",
            key: "active",
            label: "Estado",
            value: activeFilter,
            onChange: (v) => setActiveFilter(v as "" | "active" | "inactive"),
            options: [
              { label: "Todos", value: "" },
              { label: "Activo", value: "active" },
              { label: "Inactivo", value: "inactive" },
            ],
          },
        ],
        onRefresh: reload,
        refreshing: loading,
        onCreate: () => navigate("/settings/users/new"),
        createLabel: "+ Nueva cuenta",
        requires: "staff:manage-accounts:edit",
      }}
    >
      {reveal ? (
        <div className="mb-5">
          <TempPasswordReveal label={reveal.label} value={reveal.value} onDismiss={() => setReveal(null)} />
        </div>
      ) : null}

      {resetPasswordMutation.isPending && (
        <p role="status" aria-live="polite" className="sr-only">
          Generando nueva contraseña temporal...
        </p>
      )}

      {loadError && (
        <p role="alert" aria-live="assertive" className="mb-4 text-sm text-error-500">
          {loadError}
        </p>
      )}

      <DataTable
        columns={columns}
        rows={users}
        rowKey={(user) => user.id}
        loading={loading}
        emptyMessage="Sin cuentas de staff todavía."
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={total}
        onPageChange={setPage}
      />
    </ModuleLayout>
  )
}
